import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Collapse,
  Container,
  Divider,
  Grid,
  Skeleton,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import KeyboardArrowDown from "@mui/icons-material/KeyboardArrowDown";
import { Link as RouterLink } from "react-router-dom";
import { useQuery } from "react-query";
import { fetchInstagramFeed, fetchProducts } from "../endpoints";
import { Product } from "../types";
import LazyImage from "../components/LazyImage";
import { INSTAGRAM_PROFILE_URL } from "../utils/instagramFeed";
import { isStoreOpen } from "../utils/store";

/** Duration (ms): hero viewport shrinks + collection rises + fade — kept in sync */
const COLLECTION_STAGE_REVEAL_MS = 2200;

const TIKTOK_URL = "https://www.tiktok.com/@ashmy245";

const SOCIAL_IMG = {
  instagram: "/assets/instagram-social.png",
  tiktok: "/assets/tiktok-social.png",
} as const;
const INSTAGRAM_PROFILE_EMBED_URL =
  "https://www.instagram.com/inclusiviteee/?utm_source=ig_embed&utm_campaign=loading";

/** Landing copy aligned with design mockup */
const LANDING_COPY = {
  brandName: "Inclusiviteee",
  hiatusHeadline: "Be back soon",
  openHeadline: "Unity of World Religions",
  heroBody:
    "Explore our collection of t-shirts that celebrate unity and harmony across different faiths.",
  sectionCollection: "Our Collection",
  sectionInstagram: "From Instagram",
  followInstagramCta: "Follow on Instagram →",
  shopCta: "SHOP NOW",
} as const;

function envIsTrue(v: string | undefined): boolean {
  return String(v ?? "")
    .trim()
    .toLowerCase() === "true";
}

const SOCIAL_BTN_SIZE = { xs: 48, sm: 52 };

function SocialCircleLink({
  href,
  label,
  children,
  sx,
}: {
  href: string;
  label: string;
  children: React.ReactNode;
  sx?: object;
}) {
  return (
    <Box
      component="a"
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label}
      sx={{
        width: SOCIAL_BTN_SIZE,
        height: SOCIAL_BTN_SIZE,
        minWidth: SOCIAL_BTN_SIZE,
        minHeight: SOCIAL_BTN_SIZE,
        borderRadius: "50%",
        overflow: "hidden",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        textDecoration: "none",
        boxSizing: "border-box",
        bgcolor: "transparent",
        transition: "transform 0.2s ease, box-shadow 0.2s ease",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: "0 6px 16px rgba(0,0,0,0.15)",
        },
        "&:focus-visible": {
          outline: "2px solid #111",
          outlineOffset: 2,
        },
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

/** Our Collection: down arrows + title, in-flow */
function OurCollectionHeading() {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: { xs: 1.25, sm: 1.75 },
        mt: { xs: 2, md: 2 },
        mb: { xs: 3, md: 3 },
        px: { xs: 0, sm: 1 },
      }}
    >
      <KeyboardArrowDown
        sx={{ fontSize: { xs: 30, sm: 34 }, color: "text.primary" }}
        aria-hidden
      />
      <Typography
        component="h2"
        sx={{
          fontFamily: "Raleway, sans-serif",
          fontWeight: 600,
          fontSize: { xs: "1rem", sm: "1.125rem" },
          letterSpacing: "0.06em",
          color: "text.primary",
          whiteSpace: "nowrap",
          m: 0,
        }}
      >
        {LANDING_COPY.sectionCollection}
      </Typography>
      <KeyboardArrowDown
        sx={{ fontSize: { xs: 30, sm: 34 }, color: "text.primary" }}
        aria-hidden
      />
    </Box>
  );
}

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: { xs: 1.5, sm: 2 },
        my: { xs: 4, md: 5 },
        px: { xs: 0, sm: 1 },
      }}
    >
      <Divider sx={{ flex: 1, borderColor: "grey.300" }} />
      <Typography
        component="h2"
        sx={{
          fontFamily: "Raleway, sans-serif",
          fontWeight: 600,
          fontSize: { xs: "1rem", sm: "1.125rem" },
          letterSpacing: "0.06em",
          color: "text.primary",
          whiteSpace: "nowrap",
        }}
      >
        {children}
      </Typography>
      <Divider sx={{ flex: 1, borderColor: "grey.300" }} />
    </Box>
  );
}

function StarDivider() {
  const dotSx = {
    color: "#111",
    fontWeight: 800,
    fontSize: { xs: "1.1rem", sm: "1.2rem", md: "1.35rem" },
    lineHeight: 1,
    fontFamily: "Raleway, sans-serif",
  } as const;

  const starSx = {
    color: "#111",
    fontWeight: 900,
    fontSize: { xs: "1.35rem", sm: "1.55rem", md: "1.75rem" },
    lineHeight: 1,
    fontFamily: "Raleway, sans-serif",
  } as const;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: { xs: 1.25, md: 1.75 },
        width: "100%",
        my: { xs: 2.5, md: 3 },
      }}
    >
      <Box sx={{ flex: 1, height: "1px", bgcolor: "#111" }} />
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: { xs: 0.75, sm: 1 },
          flexShrink: 0,
        }}
        aria-hidden
      >
        <Typography component="span" sx={dotSx}>
          ·
        </Typography>
        <Typography component="span" sx={starSx}>
          ✴
        </Typography>
        <Typography component="span" sx={dotSx}>
          ·
        </Typography>
      </Box>
      <Box sx={{ flex: 1, height: "1px", bgcolor: "#111" }} />
    </Box>
  );
}

function InstagramProfileEmbed() {
  useEffect(() => {
    const win = window as Window & {
      instgrm?: { Embeds?: { process?: () => void } };
    };

    const processEmbeds = () => win.instgrm?.Embeds?.process?.();
    const existing = document.querySelector<HTMLScriptElement>(
      "script[src='https://www.instagram.com/embed.js']"
    );

    if (existing) {
      processEmbeds();
      return;
    }

    const script = document.createElement("script");
    script.async = true;
    script.src = "https://www.instagram.com/embed.js";
    script.onload = processEmbeds;
    document.body.appendChild(script);
  }, []);

  return (
    <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
      <blockquote
        className="instagram-media"
        data-instgrm-permalink={INSTAGRAM_PROFILE_EMBED_URL}
        data-instgrm-version="14"
        style={{
          background: "#FFF",
          border: 0,
          borderRadius: "3px",
          boxShadow:
            "0 0 1px 0 rgba(0,0,0,0.5),0 1px 10px 0 rgba(0,0,0,0.15)",
          margin: "1px",
          maxWidth: "540px",
          minWidth: "326px",
          padding: 0,
          width: "99.375%",
        }}
      >
        <a
          href={INSTAGRAM_PROFILE_EMBED_URL}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: "block",
            color: "#3897f0",
            textDecoration: "none",
            textAlign: "center",
            padding: "16px",
            fontFamily: "Arial, sans-serif",
            fontWeight: 600,
          }}
        >
          View this profile on Instagram
        </a>
      </blockquote>
    </Box>
  );
}

const Landing = () => {
  const isHiatus = envIsTrue(process.env.REACT_APP_HIATUS);
  const isShopOpen = isStoreOpen();
  const { data: instagramPayload } = useQuery(
    ["instagramFeed"],
    fetchInstagramFeed,
    { staleTime: 0, cacheTime: 60 * 1000 }
  );

  const theme = useTheme();

  const { data: products, isLoading, isError: productsError, isSuccess: productsSuccess } = useQuery(
    ["fetchProducts"],
    fetchProducts
  );
  const list = Array.isArray(products) ? (products as Product[]) : [];
  const showcase = list.slice(0, 3);

  const canRevealCollection = !productsError && productsSuccess && !isLoading;
  const [collectionMotionOn, setCollectionMotionOn] = useState(false);
  const [collectionRevealComplete, setCollectionRevealComplete] = useState(false);

  useEffect(() => {
    if (!canRevealCollection) {
      setCollectionMotionOn(false);
      setCollectionRevealComplete(false);
      return;
    }
    const id = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => setCollectionMotionOn(true));
    });
    return () => window.cancelAnimationFrame(id);
  }, [canRevealCollection]);

  useEffect(() => {
    if (!collectionMotionOn) {
      setCollectionRevealComplete(false);
      return;
    }
    const tid = window.setTimeout(
      () => setCollectionRevealComplete(true),
      COLLECTION_STAGE_REVEAL_MS
    );
    return () => window.clearTimeout(tid);
  }, [collectionMotionOn]);

  const collectionRevealSx = {
    opacity: collectionMotionOn ? 1 : 0,
    transition: theme.transitions.create("opacity", {
      duration: COLLECTION_STAGE_REVEAL_MS,
      easing: "linear",
    }),
  };

  /** Keep full-height header until the reveal timeline fully completes. */
  const viewportHeroFilled =
    !productsError && !collectionRevealComplete;

  const stageMinHeightSx = {
    transition: theme.transitions.create("min-height", {
      duration: COLLECTION_STAGE_REVEAL_MS,
      easing: theme.transitions.easing.easeInOut,
    }),
  };

  const heroShrinkSx = {
    transform: "none",
  };

  const showInstagramSection = true;

  return (
    <Box
      sx={{
        bgcolor: "#ffffff",
        color: "#111111",
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Container
        maxWidth="lg"
        sx={{
          px: { xs: 2, sm: 3 },
          pt: { xs: 2.5, md: 3 },
          pb: { xs: 6, md: 8 },
          flex: viewportHeroFilled ? 1 : undefined,
          display: "flex",
          flexDirection: "column",
          minHeight: viewportHeroFilled ? "100dvh" : 0,
          boxSizing: "border-box",
        }}
      >
        <Box
          sx={{
            position: "relative",
            flex: viewportHeroFilled ? 1 : undefined,
            display: "flex",
            flexDirection: "column",
            minHeight: viewportHeroFilled ? "100dvh" : "0px",
            ...stageMinHeightSx,
          }}
        >
          <Box
            sx={{
              flex: viewportHeroFilled ? 1 : undefined,
              display: "flex",
              flexDirection: "column",
              justifyContent: viewportHeroFilled
                ? "center"
                : "flex-start",
              minHeight: 0,
              ...heroShrinkSx,
            }}
          >
            {/* Hero — left block vertically centered with artwork on md+ (mockup) */}
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                alignItems: { xs: "stretch", md: "flex-start" },
                justifyContent: "space-between",
                gap: { xs: 2.5, md: 4 },
                minHeight: { xs: "auto", md: "auto" },
              }}
            >
          <Box
            sx={{
              width: { xs: "100%", md: "min(42%, 480px)" },
              flexShrink: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              textAlign: "left",
            }}
          >
            <Box
              sx={{
                mb: { xs: 3, md: 4 },
                display: "flex",
                alignItems: "center",
                gap: { xs: 1.25, sm: 1.5 },
                flexWrap: "wrap",
              }}
            >
              <Box
                component="img"
                src="/assets/dreamlogo.png"
                alt=""
                sx={{
                  height: { xs: 32, sm: 36 },
                  width: "auto",
                  objectFit: "contain",
                }}
              />
              <Typography
                variant="h2"
                component="h1"
                sx={{
                  fontFamily: "Raleway, sans-serif",
                  fontWeight: 700,
                  fontSize: { xs: "1.35rem", sm: "1.5rem", md: "1.625rem" },
                  letterSpacing: "-0.03em",
                  color: "#111",
                }}
              >
                {LANDING_COPY.brandName}
              </Typography>
            </Box>

            <Typography
              variant="h3"
              component="p"
              sx={{
                fontFamily: "Raleway, sans-serif",
                fontWeight: 700,
                fontSize: isHiatus
                  ? { xs: "2.25rem", sm: "2.75rem", md: "3.35rem" }
                  : { xs: "1.65rem", sm: "2rem", md: "2.5rem" },
                lineHeight: 1.12,
                letterSpacing: isHiatus ? "-0.04em" : "-0.03em",
                color: "#111",
                mb: 0,
                width: "100%",
                textAlign: isHiatus ? "center" : "left",
              }}
            >
              {isHiatus
                ? LANDING_COPY.hiatusHeadline
                : LANDING_COPY.openHeadline}
            </Typography>

            <StarDivider />

            <Typography
              sx={{
                fontFamily: "Raleway, sans-serif",
                fontWeight: 400,
                fontSize: { xs: "0.98rem", sm: "1rem", md: "1.0625rem" },
                lineHeight: 1.7,
                color: "grey.700",
                maxWidth: "100%",
                mb: { xs: 2.5, md: 3 },
              }}
            >
              {LANDING_COPY.heroBody}
            </Typography>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: { xs: 1.5, sm: 2 },
                width: "100%",
                mb: isHiatus ? 0 : 2,
              }}
            >
              <SocialCircleLink href={INSTAGRAM_PROFILE_URL} label="Instagram">
                <Box
                  component="img"
                  src={SOCIAL_IMG.instagram}
                  alt=""
                  sx={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                    display: "block",
                  }}
                />
              </SocialCircleLink>
              <SocialCircleLink href={TIKTOK_URL} label="TikTok">
                <Box
                  component="img"
                  src={SOCIAL_IMG.tiktok}
                  alt=""
                  sx={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                    display: "block",
                  }}
                />
              </SocialCircleLink>
            </Box>

            {!isHiatus && (
              <Button
                component={RouterLink}
                to="/products"
                disabled={!isShopOpen}
                variant="contained"
                size="large"
                sx={{
                  fontFamily: "Raleway, sans-serif",
                  fontWeight: 600,
                  bgcolor: "#111",
                  color: "#fff",
                  "&:hover": { bgcolor: "#333" },
                  alignSelf: { xs: "stretch", sm: "flex-start" },
                  py: 1.25,
                  px: 3,
                  boxShadow: "none",
                }}
              >
                {LANDING_COPY.shopCta}
              </Button>
            )}
          </Box>

          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minWidth: 0,
            }}
          >
            <Box
              component="img"
              src="/assets/dreamlogo.png"
              alt="Inclusiviteee artwork — unity and faith symbols"
              sx={{
                width: "100%",
                height: "auto",
                maxHeight: collectionMotionOn
                  ? { xs: "38dvh", sm: "42dvh", md: "44dvh" }
                  : "82dvh",
                transition: theme.transitions.create("max-height", {
                  duration: COLLECTION_STAGE_REVEAL_MS,
                  easing: theme.transitions.easing.easeInOut,
                }),
                objectFit: "contain",
                display: "block",
              }}
            />
          </Box>
        </Box>
          </Box>

        {/* Our Collection — hidden on error; rises as hero viewport shrinks, then enters flow */}
        {!productsError && (
          <Collapse
            in={collectionMotionOn}
            timeout={COLLECTION_STAGE_REVEAL_MS}
            easing={theme.transitions.easing.easeInOut}
            collapsedSize={0}
          >
            <Box
              sx={{
                position: "relative",
                pointerEvents: "auto",
                overflow: "hidden",
                ...collectionRevealSx,
              }}
            >
            <OurCollectionHeading />

            <Grid container spacing={{ xs: 2, sm: 3 }}>
              {isLoading &&
                [0, 1, 2].map((i) => (
                  <Grid item xs={12} sm={6} md={4} key={`sk-${i}`}>
                    <Skeleton variant="rectangular" sx={{ width: "100%", pt: "100%", borderRadius: 1 }} />
                    <Skeleton sx={{ mt: 1 }} />
                  </Grid>
                ))}
              {!isLoading &&
                showcase.map((product) => (
                  <Grid item xs={12} sm={6} md={4} key={product._id}>
                    <Box
                      component={isShopOpen ? RouterLink : "div"}
                      {...(isShopOpen ? { to: `/products/${product.slug}` } : {})}
                      sx={{
                        display: "block",
                        textDecoration: "none",
                        color: "inherit",
                        borderRadius: 1,
                        overflow: "hidden",
                        bgcolor: "#f4f4f4",
                        transition: "box-shadow 0.2s",
                        ...(isShopOpen ? { "&:hover": { boxShadow: 2 } } : {}),
                      }}
                    >
                      <Box
                        sx={{
                          position: "relative",
                          pt: "100%",
                          bgcolor: "#eaeaea",
                        }}
                      >
                        {product.images[0] && (
                          <Box
                            sx={{
                              position: "absolute",
                              inset: 0,
                              p: 2,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <LazyImage
                              src={product.images[0]}
                              alt={product.name}
                              height="100%"
                            />
                          </Box>
                        )}
                      </Box>
                      <Typography
                        sx={{
                          fontFamily: "Raleway, sans-serif",
                          fontWeight: 600,
                          fontSize: "0.95rem",
                          p: 1.5,
                          textAlign: "center",
                        }}
                      >
                        {product.name}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
            </Grid>

            {!isLoading && showcase.length === 0 && (
              <Typography color="text.secondary" sx={{ textAlign: "center", py: 2 }}>
                Collection coming soon.
              </Typography>
            )}
            </Box>
          </Collapse>
        )}
        </Box>

        {showInstagramSection && (
          <>
            <SectionHeading>{LANDING_COPY.sectionInstagram}</SectionHeading>
            <InstagramProfileEmbed />
          </>
        )}
      </Container>
    </Box>
  );
};

export default Landing;
